import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { forwardRef, type ReactNode } from "react";
import { RoundedRadioBox } from "../../components/UIToolkit/Clickable/RoundedRadioBox";
import { ALPHA, FILTER_CHIP, INK, NEUTRAL } from "../../theme/tokens";
import { presetNameSx, presetRowSx } from "./printoutPresetRowStyles";

type Props = {
	name: string;
	checked: boolean;
	/** Switching is on hold (Annie is changing the printout on screen). */
	disabled: boolean;
	/** The one radio in the group that Tab lands on (roving tabindex) — the checked one. */
	tabbable: boolean;
	onSelect: () => void;
	/** `printout-preset-select-{key}` — the preset id, or `first` for Printout 1. */
	testIdKey: string;
	/** Replaces the `printout-preset-select-{key}` test id, for a list of another kind of printout. */
	dataTestId?: string;
	/** A small marker after the name (e.g. "Not saved"). */
	trailing?: ReactNode;
};

/**
 * One printout in the printout picker: a `role="radio"` row with the radio glyph and the name. The group
 * (`PrintoutPresetBar`) owns arrow-key movement; this row is clickable and takes Space/Enter when focused.
 */
export const PrintoutPresetRow = forwardRef<HTMLDivElement, Props>(function PrintoutPresetRow(
	{ name, checked, disabled, tabbable, onSelect, testIdKey, dataTestId, trailing },
	ref,
) {
	const select = (): void => {
		if (!disabled && !checked) onSelect();
	};

	return (
		<Box
			ref={ref}
			role="radio"
			aria-checked={checked}
			aria-disabled={disabled || undefined}
			tabIndex={tabbable ? 0 : -1}
			onClick={select}
			onKeyDown={(event) => {
				if (event.key !== " " && event.key !== "Enter") return;
				event.preventDefault();
				select();
			}}
			data-testid={dataTestId === undefined ? `printout-preset-select-${testIdKey}` : dataTestId}
			sx={[
				presetRowSx(checked),
				(theme) => ({
					cursor: checked || disabled ? "default" : "pointer",
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover:not([aria-checked='true']):not([aria-disabled='true'])": { backgroundColor: NEUTRAL[50], borderColor: NEUTRAL[200] },
					},
					// The house focus ring (as on `SystemChips`' pills): a brand box-shadow, so it never shifts the row.
					"&:focus-visible": {
						outline: "none",
						boxShadow: `0 0 0 ${FILTER_CHIP.FOCUS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`,
					},
					"&[aria-disabled='true']:not([aria-checked='true']) > span": { color: INK.DISABLED },
				}),
			]}
		>
			<RoundedRadioBox checked={checked} disabled={disabled && !checked} animated aria-hidden />
			<Box component="span" sx={presetNameSx(checked)}>
				{name}
			</Box>
			{trailing}
		</Box>
	);
});
